import { createSelector } from '@reduxjs/toolkit';
import { isLengthVaryingEdge, isUntypedEdge } from 'components/tool/Facet/utils';
import { RootState } from 'store';
import { DESIGN_MODES } from '../../../constants';
import { FlattenArea, Tree } from '../TreeSlice';

const roofDataState = (state: RootState) => state.roofData.data;
const toolState = (state: RootState) => state.toolNewPostions.data;
const edgeToolState = (state: RootState) => state.roofToolLines;
const edgeToolLinesState = (state: RootState) => state.roofToolLines.data;

/**
 * In the edge tool every facet draws its own copy of the edges it owns, so a shared edge is
 * drawn twice. Classifying one only types the facet the user picked, which leaves the
 * neighbouring facet drawing the same line untyped on top of it, and whichever facet happens to
 * render last decides what the user sees. Keyed by coordId, this is the type the shared edge
 * carries on whichever side of it was classified, the user's own classification winning over an
 * auto detected one.
 */
const selectClassifiedLineTypesByCoordId = createSelector(
	[roofDataState, edgeToolLinesState],
	(roofState, linesState) => {
		const coordIdByEdgeId = new Map<number, string>();
		for (const rs of roofState.allRoofSegs) {
			for (const edge of rs.edges ?? []) {
				coordIdByEdgeId.set(edge.id, edge.coordId);
			}
		}

		const lineTypeByCoordId = new Map<string, string>();
		const claimByCoordId = new Map<string, number>();
		for (const facetId in linesState.selectedFacets) {
			for (const line of linesState.selectedFacets[facetId].selectedLines) {
				const claim = specialEdgeClaim({
					type: line.lineType,
					isSystemDetected: line.isSystemDetected
				});
				if (claim === 0) continue;

				const coordId = coordIdByEdgeId.get(line.id);
				if (!coordId || (claimByCoordId.get(coordId) ?? 0) >= claim) continue;

				claimByCoordId.set(coordId, claim);
				lineTypeByCoordId.set(coordId, line.lineType);
			}
		}

		return lineTypeByCoordId;
	}
);

type SpecialEdgeSummary = { id: number; length: number; type: string; isSystemDetected: boolean };

/**
 * How strongly a special edge record claims the shared line it sits on. An auto detected record
 * is the weakest classification: the views drop it whenever auto detection is turned off
 * (SpecialEdges, the colour legend, the edge length totals all filter on isSystemDetected), so
 * letting it win a coordId over the neighbouring facet's manual classification is what makes a
 * classified shared edge read as having no type at all.
 */
function specialEdgeClaim(specialEdge: Pick<SpecialEdges, 'type' | 'isSystemDetected'>): number {
	if (isUntypedEdge(specialEdge.type)) return 0;
	return specialEdge.isSystemDetected ? 1 : 2;
}

/**
 * Special edges are stored per roof segment edge, but a shared edge is one line on screen:
 * the two facets that meet along it each own their own edge record with the same coordId.
 * Classifying a shared edge only types the facet the user picked, so the neighbouring facet
 * keeps an untyped ('none') record for the very same coordId. Keying by coordId therefore has
 * to prefer the record with the strongest claim, otherwise whichever record happens to come
 * first in the array decides the colour and the eave renders as an unclassified grey line, or
 * as nothing at all when the record that won is a hidden auto detected one.
 */
function buildSpecialEdgesByCoordId(
	specialEdges: SpecialEdges[],
	allEdgeIdsMap: Map<number, EdgeData>
): Map<string, SpecialEdgeSummary> {
	const specialEdgesMap = new Map<string, SpecialEdgeSummary>();

	for (const specialEdge of specialEdges) {
		// a special edge can reference an edge per roof segment; any of them resolves to the
		// same coordId, so use the first one that still belongs to a non deleted segment
		const correspondingEdgeData = Object.values(specialEdge.edges ?? {})
			.map(edgeId => allEdgeIdsMap.get(edgeId))
			.find(Boolean);
		if (!correspondingEdgeData) continue;

		const existing = specialEdgesMap.get(correspondingEdgeData.coordId);
		// strongest claim wins, ties keep the record that came first
		if (existing && specialEdgeClaim(existing) >= specialEdgeClaim(specialEdge)) continue;

		specialEdgesMap.set(correspondingEdgeData.coordId, {
			id: specialEdge.id,
			type: specialEdge.type,
			length: specialEdge.length,
			isSystemDetected: specialEdge.isSystemDetected,
		});
	}

	return specialEdgesMap;
}

// Extract a shared helper and then diff the two:
function buildEdgeMap(
	allRoofSegs: RasterRoofSegment[],
	specialEdges: SpecialEdges[],
	deletedRoofIndexes: string[]
): Map<string, EdgeData> {
	const allEdgeIdsMap = new Map<number, EdgeData>();
	const uniqueEdgeMap = new Map<string, EdgeData>();

	for (const rs of allRoofSegs) {
		if (!rs.edges?.length || deletedRoofIndexes.includes(rs.id)) continue;
		for (const edge of rs.edges) {
			allEdgeIdsMap.set(edge.id, edge);
		}
	}

	const specialEdgesMap = buildSpecialEdgesByCoordId(specialEdges, allEdgeIdsMap);

	for (const rs of allRoofSegs) {
		if (!rs.edges?.length || deletedRoofIndexes.includes(rs.id)) continue;
		for (const edge of rs.edges) {
			if (uniqueEdgeMap.has(edge.coordId)) continue;

			const edgecopy = { ...edge };
			delete edgecopy['createdAt'];
			delete edgecopy['updatedAt'];

			const specialEdge = specialEdgesMap.get(edge.coordId) ?? null;
			if (specialEdge) {
				edgecopy.specialEdge = {
					id: specialEdge.id,
					type: specialEdge.type,
					isSystemDetected: specialEdge.isSystemDetected,
				};
				if (isLengthVaryingEdge(specialEdge.type)) edgecopy.length = specialEdge.length;
			}

			uniqueEdgeMap.set(edge.coordId, edgecopy);
		}
	}

	return uniqueEdgeMap;
}

const selectAllEdges = createSelector(
	[roofDataState, toolState],
	(roofState, toolNewPostions) => {

		const { allRoofSegs, specialEdges, mode } = roofState;
		const { deletedRoofIndexes } = toolNewPostions;

		const allEdgeIdsMap = new Map<number, EdgeData>();
		const uniqueEdgeMap = new Map<string, EdgeData>();

		for (const rs of allRoofSegs) {
			if (!rs.edges?.length || deletedRoofIndexes.includes(rs.id)) continue;
			for (const edge of rs.edges) {
				allEdgeIdsMap.set(edge.id, edge);
			}
		}

		const specialEdgesMap = buildSpecialEdgesByCoordId(specialEdges, allEdgeIdsMap);

		// combine edges with special edges;
		for (const rs of allRoofSegs) {
			if (!rs.edges?.length || deletedRoofIndexes.includes(rs.id)) continue;
			for (const edge of rs.edges) {
				// --- For rendering purpose  (remove duplicates)....
				if (uniqueEdgeMap.has(edge.coordId)) continue;

				const edgecopy = { ...edge };
				delete edgecopy['createdAt'];
				delete edgecopy['updatedAt'];

				const specialEdge = specialEdgesMap.get(edge.coordId) ?? null;
				if (specialEdge) {
					edgecopy.specialEdge = {
						id: specialEdge.id,
						type: specialEdge.type,
						isSystemDetected: specialEdge.isSystemDetected
					};

					// only for manual mode we consider edge lenghts othewise consider lengths 
					// calccuted using dsm
					const shouldConsiderSpecialEdgeLength = mode === DESIGN_MODES.NEARMAP_NREL_MANUAL;
					if (shouldConsiderSpecialEdgeLength && isLengthVaryingEdge(specialEdge.type)) edgecopy.length = specialEdge.length;
				}

				uniqueEdgeMap.set(edge.coordId, edgecopy);
			}
		}

		return Array.from(uniqueEdgeMap.values());
	});

const selectUpdatedEdges = createSelector(
	[roofDataState, toolState, edgeToolState],
	(roofState, toolNewPositions, edgeToolState) => {
		const { allRoofSegs, specialEdges: newSpecialEdges } = roofState;
		const prevSpecialEdges = edgeToolState.prevStoredState.specialEdges;
		const { deletedRoofIndexes } = toolNewPositions;

		const specialEdgesById = new Map<number, SpecialEdges>();
		for (const se of newSpecialEdges) {
			specialEdgesById.set(se.id, se);
		}

		const newEdgeMap = buildEdgeMap(allRoofSegs, newSpecialEdges, deletedRoofIndexes);
		const prevEdgeMap = buildEdgeMap(allRoofSegs, prevSpecialEdges, deletedRoofIndexes);

		const newEdgeEntries = Array.from(newEdgeMap.entries());

		const updatedEdges: SpecialEdges[] = [];

		for (const [coordId, newEdge] of newEdgeEntries) {
			const prevEdge = prevEdgeMap.get(coordId);

			const prevSpecialEdgeType = prevEdge?.specialEdge?.type ?? null;
			const newSpecialEdgeTYpe = newEdge?.specialEdge?.type ?? null;

			if(!newEdge.specialEdge?.id) continue;
			if (prevSpecialEdgeType !== newSpecialEdgeTYpe) {
				const specialEdge = specialEdgesById.get(newEdge.specialEdge.id);
				if(specialEdge){
					updatedEdges.push(specialEdge);
				}
			}
		}

		return updatedEdges;
	}
);

const selectGroundMeterPerPixel = createSelector(
	[roofDataState], (roofstate) => {
		const { groundMetersPerPixel } = roofstate;
		// const {customMaps , selectedMap} = customMapState;

		// const visibleLayers = customMaps.filter((map) => map.isVisible);

		// if (visibleLayers.length === 0) return groundMetersPerPixel;

		// // Sort by sortOrder descending to get top layer first
		// visibleLayers.sort((a, b) => b.sortOrder - a.sortOrder);

		// let customMapScale = visibleLayers[0].metersPerPixel;
		// if(selectedMap) customMapScale = selectedMap.metersPerPixel;
		return groundMetersPerPixel;
	}
);
const selectPxScale = createSelector(
	[selectGroundMeterPerPixel],
	(metersPerPixel) => {
		return metersPerPixel * 10;
	}
);

const selectFilteredGutterLines = createSelector(
	[roofDataState, toolState] , (roofData, toolEditedData) =>{
		const deletedRoofIndexes = toolEditedData.deletedRoofIndexes;
		return roofData.allGutterLines.filter((g)=>{
			if(g.roofSegmentId && deletedRoofIndexes.includes(g.roofSegmentId)) return false;
			if (!g.userMapped) return roofData.showAutoDetectedEdges;
			return true;
		});
	}
);

const selectUpdatedGutterLines = createSelector([roofDataState, edgeToolState], 
	(roofState, edgeToolState) => {
		const newGutterLines = roofState.allGutterLines;
		const prevGutterLines = edgeToolState.prevStoredState.gutterLines;

		// Build lookup key from coords + roofSegmentId
		const prevGutterLineKeys = new Set<string>();
		for (const gl of prevGutterLines) {
			const key = `${gl.roofSegmentId}-${JSON.stringify(gl.coords)}`;
			prevGutterLineKeys.add(key);
		}

		// Find new gutter lines that didn't exist in prev
		const addedGutterLines: GutterLine[] = [];
		for (const gl of newGutterLines) {
			const key = `${gl.roofSegmentId}-${JSON.stringify(gl.coords)}`;
			if (!prevGutterLineKeys.has(key)) {
				addedGutterLines.push(gl);
			}
		}

		return addedGutterLines;
	}
);

const selectDsmEdits = createSelector([roofDataState], (design) => {
	const { dsmEdits, groundMetersPerPixel } = design;
	const trees: Tree[] = dsmEdits.filter(e => e.type === 'TREE').map((e) => {
		const { trunkHeight, ...edits } = e.edits as TreeType;
		return {
			...edits,
			id: e.id,
			treeHeight: trunkHeight + edits.crownHeight,
			crownRadius: edits.crownRadius / groundMetersPerPixel,
			trunkRadius: edits.trunkRadius / groundMetersPerPixel,
		};
	});

	const flattenArea: FlattenArea[] = dsmEdits.filter(e => e.type === 'FLATTEN_AREA').map(e => (
		{
			...e.edits as FlattenArea,
			id: e.id
		})
	);

	return {
		trees, flattenArea
	};
});
export {
	selectAllEdges,
	selectClassifiedLineTypesByCoordId,
	selectGroundMeterPerPixel,
	selectPxScale,
	selectFilteredGutterLines,
	selectUpdatedEdges,
	selectUpdatedGutterLines,
	selectDsmEdits
};